import { recruitingStageChipSx } from "../utils/pipelineStages";
import React from "react";
import { Box, Chip, Divider, Link, Paper, Stack, Typography } from "@mui/material";
import DOMPurify from "dompurify";
import { portalApiUrl, safePortalLink } from "../utils/clientPortal";

const prose = (value) => DOMPurify.sanitize(String(value || ""), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
function Field({ label, value }) {
  return <Box><Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{label}</Typography>
    <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{prose(value) || "Not provided"}</Typography></Box>;
}
function Section({ title, children }) {
  return <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
    <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>{title}</Typography>{children}</Paper>;
}
export default function ClientCandidatePresentation({ data }) {
  const { candidate: c, screen: s, job_match: j } = data;
  const linkedin = safePortalLink(c.linkedin_url);
  return <Stack spacing={3}>
    <Section title="Candidate">
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2}>
        <Box><Typography variant="h4" sx={{ fontWeight: 700 }}>{c.name}</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>{[c.title, c.company].filter(Boolean).join(" · ")}</Typography>
          <Typography color="text.secondary">{c.location}</Typography></Box>
        <Chip label={j.stage || "Presented"} sx={recruitingStageChipSx(j.stage || "Presented")} />
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Field label="Full RPO Candidate Summary" value={c.summary} />
      <Stack direction="row" spacing={3} sx={{ my: 3 }}>
        {c.has_resume && <Link href={portalApiUrl(`/presentations/${data.id}/resume`)} target="_blank" rel="noopener noreferrer">Download resume</Link>}
        {linkedin && <Link href={linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</Link>}
      </Stack>
      {data.permissions.can_view_contact ? <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
        <Field label="Email" value={c.email} /><Field label="Phone" value={c.phone} />
      </Stack> : <Typography variant="body2" color="text.secondary">Contact your recruiter to arrange an introduction.</Typography>}
    </Section>
    <Section title="Recruiter Screen">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        <Field label="Reason for looking" value={s.reason_for_looking} />
        <Field label="Motivation" value={s.motivation} />
        <Field label="Compensation expectations" value={s.compensation} />
        <Field label="Availability" value={s.availability} />
        <Field label="Timeline" value={s.timeline} />
        <Field label="Work authorization" value={s.work_authorization} />
        <Field label="Sponsorship" value={s.sponsorship} />
        <Field label="Interview availability" value={s.interview_availability} />
      </Box>
      <Divider sx={{ my: 3 }} /><Field label="Approved presentation notes" value={s.approved_notes} />
      <Divider sx={{ my: 3 }} /><Typography sx={{ fontWeight: 600, mb: 2 }}>Relevant technical / functional skills</Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
        {s.skills.length ? s.skills.map((skill, index) => <Chip key={`${skill}-${index}`} label={skill} variant="outlined" />) : <Typography color="text.secondary">Not provided</Typography>}
      </Stack>
      <Divider sx={{ my: 3 }} /><Typography sx={{ fontWeight: 600, mb: 2 }}>Relevant experience</Typography>
      <Stack spacing={3}>{s.relevant_experience.length ? s.relevant_experience.map((e, index) => <Box key={index}>
        <Typography sx={{ fontWeight: 600 }}>{[e.title, e.company].filter(Boolean).join(" · ")}</Typography>
        <Typography variant="body2" color="text.secondary">{[e.from, e.to].filter(Boolean).join(" – ")}</Typography>
        <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>{prose(e.summary)}</Typography>
        {e.bullets.length > 0 && <Box component="ul" sx={{ pl: 2.5 }}>{e.bullets.map((bullet, i) => <li key={i}>{prose(bullet)}</li>)}</Box>}
      </Box>) : <Typography color="text.secondary">Not provided</Typography>}</Stack>
    </Section>
    <Section title="Job Match">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        <Field label="Job" value={j.title} /><Field label="Company" value={j.company} />
        <Field label="Candidate stage" value={j.stage} /><Field label="Recruiter / owner" value={j.recruiter} />
        <Field label="Match score" value={j.match_score == null ? "" : `${j.match_score}%`} />
      </Box>
      <Divider sx={{ my: 3 }} /><Field label="Match details" value={j.match_details} />
      <Box sx={{ mt: 3 }}><Field label="Why RPO recommends this candidate" value={j.recommendation} /></Box>
    </Section>
  </Stack>;
}
